/**
 * Regras de apresentação da nota NSTech no painel Status integrações
 * (TMS-7222, A3 e A6). Funções puras: sem Vue, sem HTTP, sem relógio implícito
 * (o "agora" entra por parâmetro) — é o que permite auditá-las sem runner.
 *
 * Contrato aditivo da linha NSTech (ADR-0014 D2): `docfis_id`, `motivo`,
 * `orientacao`, `dt_ultima_tentativa`, `em_processamento_desde`,
 * `reenvio_permitido`, `reenvio_em_andamento`. Backend antigo não os envia — cada função devolve o
 * "nada" correto quando o campo falta, para este front poder subir antes.
 */
import {
  DETALHE_CARGA,
  DETALHE_MENSAGEM,
  DETALHE_NOTA,
  DETALHE_ULTIMA_TENTATIVA,
  ESPERA_EM_PROCESSAMENTO,
  ESPERA_MENOS_DE_UM_MINUTO,
  TITULO_NOTA_NAO_ENVIADA,
  TITULO_NOTA_RECUSADA,
} from "../constants/nstechNotaCopy";

/** `status.valor` que o backend usa para "nota sem integração" (sem linha). */
export const STATUS_SEM_INTEGRACAO = -2;

function preenchido(valor) {
  return valor !== null && valor !== undefined && String(valor).trim() !== "";
}

/** Há motivo de recusa para mostrar? */
export function temMotivo(item) {
  return !!item && preenchido(item.motivo);
}

/**
 * "Não enviada" quando a nota não tem integração (`status.valor === -2`) OU não
 * tem registro de recusa (sem `motivo`: nunca foi despachada). "Recusada" só
 * quando há motivo e a nota tem integração.
 */
export function tituloDaNota(item) {
  const semIntegracao =
    item && item.status && item.status.valor === STATUS_SEM_INTEGRACAO;

  return semIntegracao || !temMotivo(item) || !preenchido(item.num_doc)
    ? TITULO_NOTA_NAO_ENVIADA
    : TITULO_NOTA_RECUSADA.replace("{numDoc}", String(item.num_doc));
}

/**
 * Só `=== true`: campo ausente (backend antigo) não mostra botão. O bloqueio
 * real é do endpoint (404/409/422); aqui é só interface.
 */
export function podeReenviarNota(item) {
  return !!item && item.reenvio_permitido === true;
}

/**
 * Há job de reenvio desta nota em voo? Só `=== true`: campo ausente (backend antigo)
 * é "não está em andamento". O back não altera `reenvio_permitido` nesse caso; quem
 * impede o clique repetido é a interface (o endpoint já devolve 409).
 */
export function reenvioEmAndamento(item) {
  return !!item && item.reenvio_em_andamento === true;
}

/**
 * Alguma nota da lista NSTech da carga tem reenvio em voo? A view usa para manter o
 * painel atualizando até o job terminar (o painel não se atualiza sozinho).
 */
export function algumReenvioEmAndamento(notas) {
  const itens = Array.isArray(notas)
    ? notas
    : notas && typeof notas === "object"
      ? Object.values(notas)
      : [];

  return itens.some((nota) => reenvioEmAndamento(nota));
}

/**
 * O componente renderiza algo? Motivo, botão de reenvio (nota nunca despachada
 * não tem motivo) ou contador de espera. Sem nenhum dos campos novos do back,
 * nada — a célula fica idêntica a antes.
 */
export function deveRenderizarNota(item, agora) {
  return (
    temMotivo(item) ||
    podeReenviarNota(item) ||
    reenvioEmAndamento(item) ||
    textoDeEspera(item ? item.em_processamento_desde : null, agora) !== null
  );
}

/**
 * "há N min" a partir de `em_processamento_desde` (ISO8601). Devolve `null`
 * quando não há o que mostrar: campo ausente ou data inválida.
 */
export function textoDeEspera(emProcessamentoDesde, agora) {
  if (!preenchido(emProcessamentoDesde)) {
    return null;
  }

  const desde = new Date(emProcessamentoDesde).getTime();
  const ate = agora instanceof Date ? agora.getTime() : Number(agora);

  if (Number.isNaN(desde) || Number.isNaN(ate)) {
    return null;
  }

  const minutos = Math.max(0, Math.floor((ate - desde) / 60000));
  const tempo =
    minutos < 1 ? ESPERA_MENOS_DE_UM_MINUTO : formataDuracao(minutos);

  return ESPERA_EM_PROCESSAMENTO.replace("{tempo}", tempo);
}

function formataDuracao(minutos) {
  if (minutos < 60) {
    return `${minutos} min`;
  }

  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;

  return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`;
}

function dataLegivel(iso) {
  if (!preenchido(iso)) {
    return null;
  }

  const data = new Date(iso);

  if (Number.isNaN(data.getTime())) {
    return null;
  }

  const dois = (n) => String(n).padStart(2, "0");

  return `${dois(data.getDate())}/${dois(data.getMonth() + 1)}/${data.getFullYear()} ${dois(data.getHours())}:${dois(data.getMinutes())}`;
}

/**
 * Linhas do bloco recolhido "Ver detalhes". Só o que o backend entrega: carga,
 * nota, horário e a mensagem da NSTech — sem payload cru (ADR-0014, ameaças:
 * PII). Valor ausente é descartado, como no precedente.
 */
export function detalhesDaNota(item, cargaId) {
  if (!item) {
    return [];
  }

  return [
    { rotulo: DETALHE_CARGA, valor: preenchido(cargaId) ? String(cargaId) : null },
    {
      rotulo: DETALHE_NOTA,
      valor: preenchido(item.num_doc)
        ? preenchido(item.serie)
          ? `${item.num_doc} - ${item.serie}`
          : String(item.num_doc)
        : null,
    },
    { rotulo: DETALHE_ULTIMA_TENTATIVA, valor: dataLegivel(item.dt_ultima_tentativa) },
    { rotulo: DETALHE_MENSAGEM, valor: preenchido(item.motivo) ? String(item.motivo) : null },
  ].filter((linha) => linha.valor !== null);
}
